import type {
  AppointmentSeriesLink,
  SeriesApiError,
  SeriesCode,
  SeriesListCounts,
  SeriesListPage,
  SeriesListRow,
  SeriesPerson,
  SeriesPreviewVisit,
  SeriesRun,
  SeriesRunRow,
  SeriesRunVisit,
  SeriesSaveWarning,
  SeriesStatus,
  SeriesTemplate,
  SeriesType,
  SeriesUnit,
  SeriesVisitBucket,
  SeriesVisitPerson,
  SeriesWarning,
} from './series.types'
import {
  MAX_EVERY,
  MAX_SERIES_VISITS,
  SERIES_LIST_PER_PAGE,
  SERIES_TEXT,
} from 'features/Series/series.constants'

type Row = Record<string, unknown>

const UNITS: SeriesUnit[] = ['day', 'week', 'month']
const STATUSES: SeriesStatus[] = ['active', 'completed', 'cancelled']
const TYPES: SeriesType[] = ['series', 'protocol']
const BUCKETS: SeriesVisitBucket[] = ['completed', 'noshow', 'cancelled', 'scheduled', 'unbooked']
const GENERIC_ERROR = SERIES_TEXT.requestFailed
const EMR_UNAVAILABLE = 'emr_unavailable'
const REQUEST_FAILED = 'request_failed'
const FETCH_ERROR = 'FETCH_ERROR'
const TIMEOUT_ERROR = 'TIMEOUT_ERROR'
const SERVER_ERROR_MIN = 500

const asRow = (value: unknown): Row | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Row) : null

const asList = (value: unknown): unknown[] => (Array.isArray(value) ? value : [])

const readText = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value : null

const readInt = (value: unknown): number | null =>
  typeof value === 'number' && Number.isSafeInteger(value) ? value : null

const readNumber = (value: unknown, fallback = 0): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback

// Server counts drive loops and steppers, so they stay whole and in range.
const readClamped = (value: unknown, min: number, max: number): number =>
  Math.min(max, Math.max(min, Math.trunc(readNumber(value, min))))

const readOneOf = <T extends string>(value: unknown, allowed: T[]): T | null =>
  allowed.find((item) => item === value) ?? null

const readNamed = (value: unknown): { id: number; name: string } | null => {
  const row = asRow(value)
  const id = readInt(row?.id)
  const name = readText(row?.name)
  return id === null || name === null ? null : { id, name }
}

const readCode = (value: unknown): SeriesCode | null => {
  const row = asRow(value)
  const type = readText(row?.type)
  const code = readText(row?.code)
  if (type === null || code === null) return null
  return { type, code, description: readText(row?.description) }
}

const readPerson = (value: unknown): SeriesPerson => {
  const row = asRow(value)
  return {
    id: readInt(row?.id),
    name: readText(row?.name),
    emrId: readInt(row?.emr_pid ?? row?.emr_id),
  }
}

export const toAppointmentSeriesLink = (value: unknown): AppointmentSeriesLink | null => {
  const row = asRow(value)
  if (!row) return null
  const id = readInt(row.id)
  const step = readInt(row.step)
  const total = readInt(row.total)
  const type = readOneOf(row.type, TYPES)
  if (id === null || step === null || total === null || type === null) return null
  return { id, step, total, type }
}

const readTemplate = (value: unknown): SeriesTemplate | null => {
  const row = asRow(value)
  const id = readInt(row?.id)
  const name = readText(row?.name)
  if (!row || id === null || name === null) return null
  return {
    id,
    name,
    description: readText(row.description),
    visits: readClamped(row.visits, 0, MAX_SERIES_VISITS),
    every: readClamped(row.every, 1, MAX_EVERY),
    unit: readOneOf(row.unit, UNITS) ?? 'week',
    cadenceLabel: readText(row.cadence_label),
    duration: readNumber(row.duration),
    category: readNamed(row.category),
    facility: readNamed(row.facility),
    billingCode: readCode(row.billing_code),
    issueCodes: asList(row.issue_codes)
      .map(readCode)
      .filter((code): code is SeriesCode => code !== null),
    active: row.active !== false,
    inUse: readNumber(row.in_use),
  }
}

export const transformTemplates = (response: unknown): SeriesTemplate[] =>
  asList(asRow(response)?.templates)
    .map(readTemplate)
    .filter((template): template is SeriesTemplate => template !== null)

// A write answers {template}; an unreadable one fails the call instead of faking data.
export const transformTemplate = (response: unknown): SeriesTemplate => {
  const template = readTemplate(asRow(response)?.template)
  if (template === null) throw new Error(GENERIC_ERROR)
  return template
}

// A warning without a code still says something, and may still be a refusal.
const readWarning = (value: unknown): SeriesWarning | null => {
  const row = asRow(value)
  const message = readText(row?.message)
  return message === null
    ? null
    : { code: readText(row?.code) ?? '', message, refused: row?.refused === true }
}

const readPreviewVisit = (value: unknown): SeriesPreviewVisit | null => {
  const row = asRow(value)
  const step = readInt(row?.step)
  const date = readText(row?.date)
  const time = readText(row?.time)
  if (!row || step === null || date === null || time === null) return null
  return {
    step,
    date,
    time,
    duration: readNumber(row.duration),
    warnings: asList(row.warnings)
      .map(readWarning)
      .filter((warning): warning is SeriesWarning => warning !== null),
  }
}

export const transformPreview = (response: unknown): SeriesPreviewVisit[] =>
  asList(asRow(response)?.visits)
    .map(readPreviewVisit)
    .filter((visit): visit is SeriesPreviewVisit => visit !== null)

const readVisitPerson = (value: unknown): SeriesVisitPerson | null => {
  const row = asRow(value)
  if (!row || !asRow(row.doctor)) return null
  return {
    doctor: readPerson(row.doctor),
    appointmentId: readInt(row.appointment_id),
    emrAppointmentId: readInt(row.emr_appointment_id),
    isPrimary: row.is_primary === true,
  }
}

// Older answers carry only the visit's own doctor, who is then the primary.
const readVisitPeople = (row: Record<string, unknown>): SeriesVisitPerson[] => {
  if (Array.isArray(row.people)) {
    return row.people
      .map(readVisitPerson)
      .filter((person): person is SeriesVisitPerson => person !== null)
  }
  if (!asRow(row.doctor)) return []
  return [
    {
      doctor: readPerson(row.doctor),
      appointmentId: readInt(row.appointment_id),
      emrAppointmentId: readInt(row.emr_appointment_id),
      isPrimary: true,
    },
  ]
}

const readRunVisit = (value: unknown): SeriesRunVisit | null => {
  const row = asRow(value)
  const step = readInt(row?.step)
  if (!row || step === null) return null
  const appointmentId = readInt(row.appointment_id)
  const bucket =
    readOneOf(row.bucket, BUCKETS) ?? (appointmentId === null ? 'unbooked' : 'scheduled')
  return {
    step,
    appointmentId,
    emrAppointmentId: readInt(row.emr_appointment_id),
    date: readText(row.date),
    time: readText(row.time),
    duration: readNumber(row.duration),
    doctor: asRow(row.doctor) ? readPerson(row.doctor) : null,
    people: readVisitPeople(row),
    status: readText(row.status),
    bucket,
    recallDate: readText(asRow(row.recall)?.date),
  }
}

const readNextVisit = (value: unknown): { date: string; time: string } | null => {
  const row = asRow(value)
  const date = readText(row?.date)
  const time = readText(row?.time)
  return date === null || time === null ? null : { date, time }
}

const readRunRow = (value: unknown): SeriesRunRow | null => {
  const row = asRow(value)
  const id = readInt(row?.id)
  if (!row || id === null) return null
  return {
    id,
    type: readOneOf(row.type, TYPES) ?? 'series',
    name: readText(row.name) ?? '',
    status: readOneOf(row.status, STATUSES) ?? 'active',
    startedOn: readText(row.started_on),
    patient: readPerson(row.patient),
    doctor: readPerson(row.doctor),
    cadenceLabel: readText(row.cadence_label),
    total: readClamped(row.total, 0, MAX_SERIES_VISITS),
    completed: readNumber(row.completed),
    progressPct: readNumber(row.progress_pct),
    nextVisit: readNextVisit(row.next_visit),
  }
}

const readListRow = (value: unknown): SeriesListRow | null => {
  const base = readRunRow(value)
  return base === null ? null : { ...base, status: readText(asRow(value)?.status) ?? base.status }
}

const readListCounts = (value: unknown): SeriesListCounts => {
  const row = asRow(value)
  return {
    total: readNumber(row?.total),
    active: readNumber(row?.active),
    completed: readNumber(row?.completed),
    cancelled: readNumber(row?.cancelled),
    upcomingVisits: readNumber(row?.upcoming_visits),
  }
}

export const transformSeriesList = (response: unknown): SeriesListPage => {
  const body = asRow(response)
  const meta = asRow(body?.meta)
  return {
    rows: asList(body?.data)
      .map(readListRow)
      .filter((row): row is SeriesListRow => row !== null),
    counts: readListCounts(body?.counts),
    meta: {
      page: readNumber(meta?.page, 1),
      perPage: readNumber(meta?.per_page, SERIES_LIST_PER_PAGE),
      total: readNumber(meta?.total),
    },
  }
}

const readTallies = (value: unknown): Record<SeriesVisitBucket, number> => {
  const row = asRow(value)
  return {
    completed: readNumber(row?.completed),
    noshow: readNumber(row?.noshow),
    cancelled: readNumber(row?.cancelled),
    scheduled: readNumber(row?.scheduled),
    unbooked: readNumber(row?.unbooked),
  }
}

const readRun = (value: unknown): SeriesRun | null => {
  const base = readRunRow(value)
  const row = asRow(value)
  if (!base || !row) return null
  return {
    ...base,
    note: typeof row.note === 'string' ? row.note : '',
    issues: asList(row.issues).flatMap((issue) => {
      const item = asRow(issue)
      const id = readInt(item?.id)
      return id === null ? [] : [{ id, title: readText(item?.title) ?? '' }]
    }),
    visits: asList(row.visits)
      .map(readRunVisit)
      .filter((visit): visit is SeriesRunVisit => visit !== null),
    tallies: readTallies(row.tallies),
  }
}

export const transformRun = (response: unknown): SeriesRun | null =>
  readRun(asRow(response)?.series)

const readSaveWarning = (value: unknown): SeriesSaveWarning | null => {
  const row = asRow(value)
  const message = readText(row?.message)
  return message === null ? null : { step: readInt(row?.step_sequence), message }
}

// OpenEMR sends { pc_eid, step_sequence, lock_type }; a bare number also passes.
const readLockedStep = (value: unknown): number | null => {
  const row = asRow(value)
  return readInt(row?.step_sequence ?? row?.step ?? value)
}

export const transformSaveResult = (
  response: unknown,
): { series: SeriesRun | null; warnings: SeriesSaveWarning[]; locked: number[] } => {
  const body = asRow(response)
  return {
    series: readRun(body?.series),
    warnings: asList(body?.warnings)
      .map(readSaveWarning)
      .filter((warning): warning is SeriesSaveWarning => warning !== null),
    locked: asList(body?.locked)
      .map(readLockedStep)
      .filter((step): step is number => step !== null),
  }
}

const readErrors = (value: unknown): Record<string, string[]> => {
  const row = asRow(value)
  if (!row) return {}
  return Object.fromEntries(
    Object.entries(row).map(([key, messages]) => [
      key,
      asList(messages).filter((message): message is string => typeof message === 'string'),
    ]),
  )
}

// No answer at all: a 5xx, a network failure, a timeout or no response.
const isUnanswered = (status: number | string): boolean =>
  status === 0 ||
  status === FETCH_ERROR ||
  status === TIMEOUT_ERROR ||
  (typeof status === 'number' && status >= SERVER_ERROR_MIN)

// Accepts the RTK error object and an unwrap() rejection (same shape).
export const toSeriesApiError = (error: unknown): SeriesApiError => {
  const row = asRow(error)
  const status = typeof row?.status === 'number' || typeof row?.status === 'string' ? row.status : 0
  const body = asRow(row?.data)
  const code = readText(body?.code)
  if (code === null && isUnanswered(status)) {
    return {
      status,
      code: EMR_UNAVAILABLE,
      message: readText(body?.message) ?? GENERIC_ERROR,
      errors: {},
    }
  }
  return {
    status,
    code: code ?? REQUEST_FAILED,
    message: readText(body?.message) ?? GENERIC_ERROR,
    errors: readErrors(body?.errors),
  }
}
